import React, {Component} from 'react';
import MapMarkers from './map/MapMarkers';
import './Map.css';

const DEFAULT_ZOOM = 12;
const MIN_ZOOM = 18;

interface MapProps {
    receiverLatLng: google.maps.LatLngLiteral;
    driverLatLng: google.maps.LatLngLiteral | null;
    driverBearing: number;
}

interface MapState {
    mapReady: boolean;
}

class Map extends Component<MapProps, MapState> {

    static GOOGLE_MAPS_SCRIPT_ID = "googleMapsScript";

    // Assigned in onMapLoaded() once the Google Maps script has loaded
    private map!: google.maps.Map;
    private driverMarker?: google.maps.Marker;

    constructor(props: MapProps) {
        super(props);
        this.state = {
            mapReady: false
        }
    }

    override componentDidMount() {
        const existingScript = document.getElementById(Map.GOOGLE_MAPS_SCRIPT_ID);
        if (!existingScript) {
            this.loadGoogleMaps();
        }
    }

    loadGoogleMaps = () => {
        const apiKey = 'AIzaSyDdxmkaMfqrHKgGd_7y2uFLnAfo63VFMMc';
        const script = document.createElement('script');
        script.id = Map.GOOGLE_MAPS_SCRIPT_ID;
        script.src = `//maps.googleapis.com/maps/api/js?key=${apiKey}`;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', () => {
            this.setState({ mapReady: true });
        });
        document.body.appendChild(script);
    }

    override componentDidUpdate(prevProps: MapProps, prevState: MapState) {
        if (prevState.mapReady !== this.state.mapReady) {
            this.onMapLoaded();
        }
        if ((!prevProps.driverLatLng && this.props.driverLatLng)) {
            this.addDriverLocationMarker();
        }
        if (prevProps.driverLatLng && !this.props.driverLatLng) {
            this.removeDriverLocationMarker();
        }
        if (prevProps.driverLatLng && this.props.driverLatLng) {
            if ((prevProps.driverLatLng.lat !== this.props.driverLatLng.lat) || (prevProps.driverLatLng.lng !== this.props.driverLatLng.lng) || (prevProps.driverBearing !== this.props.driverBearing)) {
                this.moveDriverLocation(prevProps.driverLatLng, this.props.driverLatLng, this.props.driverBearing, this.props.receiverLatLng);
            }
        }
    }

    onMapLoaded = () => {
        const googleMaps = google.maps;
        const deliveryLatLng = new googleMaps.LatLng(this.props.receiverLatLng.lat, this.props.receiverLatLng.lng);
        this.map = new googleMaps.Map(document.getElementById('map') as HTMLElement, {
            zoom: DEFAULT_ZOOM,
            gestureHandling: 'cooperative',
            streetViewControl: false,
            mapTypeId: googleMaps.MapTypeId.ROADMAP
        });
        const imageUri = 'data:image/svg+xml;charset=utf-8,' + MapMarkers.deliveryLocationNewSVG_URIEncoded;
        const markerIcon = {
            url: imageUri,
            size: new googleMaps.Size(90, 90),
            // The anchor for this image is the base of the flagpole
            anchor: new googleMaps.Point(28, 40)
        };
        new googleMaps.Marker({
            map: this.map,
            title: "Delivery Location",
            optimized: false, // Required for svg markers
            position: deliveryLatLng,
            icon: markerIcon
        });
        googleMaps.event.addListener(this.map, 'zoom_changed', function(this: google.maps.Map) {
            if ((this.getZoom() ?? 0) > MIN_ZOOM) {
                this.setZoom(MIN_ZOOM);
            }
        });

        if (this.props.driverLatLng) {
            this.addDriverLocationMarker();
        } else {
            this.map.setCenter(deliveryLatLng);
        }
    };

    buildDriverIcon = (): google.maps.Icon => {
        const driverBearing = Math.round(this.props.driverBearing || 0);
        const googleMaps = google.maps;
        const imageUri = 'data:image/svg+xml;charset=utf-8,' + MapMarkers.driverLocationNewSVG_URIEncoded(driverBearing);
        return {
            url: imageUri,
            size: new googleMaps.Size(60, 60),
            // anchor should be the center of the truck
            anchor: new googleMaps.Point(26, 26)
        };
    }

    addDriverLocationMarker = () => {
        // console.log("addDriverLocationMarker", this.props, this.state);
        if (!this.props.driverLatLng) {
            return;
        }
        const googleMaps = google.maps;
        const bounds = new googleMaps.LatLngBounds();
        bounds.extend(new googleMaps.LatLng(this.props.receiverLatLng.lat, this.props.receiverLatLng.lng));
        const driverLatLng = new googleMaps.LatLng(this.props.driverLatLng.lat, this.props.driverLatLng.lng);
        this.driverMarker = new googleMaps.Marker({
            map: this.map,
            title: "Driver Location",
            position: driverLatLng,
            optimized: false, // Required for svg markers
            icon: this.buildDriverIcon()
        });
        bounds.extend(driverLatLng);
        this.map.fitBounds(bounds);
    };

    removeDriverLocationMarker = () => {
        if (this.driverMarker) {
            this.driverMarker.setMap(null);
            this.map.setCenter(new google.maps.LatLng(this.props.receiverLatLng.lat, this.props.receiverLatLng.lng));
        }
    };

    moveDriverLocation = (
        prevLatLng: google.maps.LatLngLiteral,
        toLatLng: google.maps.LatLngLiteral,
        driverBearing: number,
        receiverLatLng: google.maps.LatLngLiteral
    ) => {
        // console.log("moveDriverLocation", prevLatLng, toLatLng, driverBearing, receiverLatLng);
        const googleMaps = google.maps;
        const driverMarker = this.driverMarker!;
        const map = this.map;

        driverMarker.setIcon(this.buildDriverIcon())

        function moveMarker(i: number, numDeltas: number, currentLatLng: google.maps.LatLngLiteral, deltaLatLng: google.maps.LatLngLiteral) {
            currentLatLng.lat += deltaLatLng.lat;
            currentLatLng.lng += deltaLatLng.lng;
            const driverLatLng = new googleMaps.LatLng(currentLatLng.lat, currentLatLng.lng);
            driverMarker.setPosition(driverLatLng);
            if (i !== numDeltas){
                i++;
                setTimeout(moveMarker, delay, i, numDeltas, currentLatLng, deltaLatLng);
            } else {
                const bounds = new googleMaps.LatLngBounds();
                bounds.extend(driverLatLng);
                bounds.extend(new googleMaps.LatLng(receiverLatLng.lat, receiverLatLng.lng));
                map.fitBounds(bounds);
            }
        }

        const numDeltas = 100;
        const delay = 10; //milliseconds
        const i = 0;
        const deltaLatLng = {
            lat: (toLatLng.lat - prevLatLng.lat)/numDeltas,
            lng: (toLatLng.lng - prevLatLng.lng)/numDeltas
        };
        // Animate a copy, moveMarker() updates the position in place and prevLatLng belongs to the previous props
        setTimeout(moveMarker, delay, i, numDeltas, {...prevLatLng}, deltaLatLng);
    };

    override render() {
        return (
            <div id="map" />
        )
    }
}

export default Map;
